import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  alpha,
  Tooltip,
  Stack,
} from "@mui/material";
import {
  formatAmount,
  formatHistoryAmount,
  getCommaOut,
} from "../../../utils/hooks/dataHandler";
import PdfGenerator from "./Pdf/FaSummaryPdf";
import ScheduleHeader from "../../App/ScheduleHeader";
import ComponentLoading from "../../App/ComponentLoading";
import {
  TextFieldForName,
  TextFieldForViewOnly,
} from "../../utils/TextField/TextField";
import { CalculatorTextField } from "../../utils/TextField/NumericTextField";
import ScheduleNotes from "../ScheduleNotes";
import { v4 as uuidv4 } from "uuid";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LinkIcon from "@mui/icons-material/Link";
import RefreshIcon from "@mui/icons-material/Refresh";
import IconButtonWithTooltip from "../../utils/IconButtonWithTooltip";
import { getHeaderStyles } from "../utils/ScheduleContent";
import ViewAgendaIcon from "@mui/icons-material/ViewAgenda";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { JustInfo, StyledIconButton } from "../../utils/Icon";
import CustomDialog from "../../utils/dialog/customDialog";
import NoDataAvailableUI from "../../Common/NoDataAvailable";
import Toolbar from "../../utils/Toolbar";
import FullscreenDialog from "../../utils/dialog/FullscreenDialog";
import { exportComponentAsImage } from "../../../utils/export/componentExporter";
import FaSummaryExcelUpload from "./FaSummaryExcelUpload";

const COST_TYPE = "Cost";
const ACC_DEPRE_TYPE = "Accumulated Depreciation";

const sectionStyle = (theme) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.04),
  color: theme.palette.text.primary,
  fontWeight: 600,
  textAlign: "left",
  padding: "20px 24px",
  letterSpacing: "0.1px",
  borderTop: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "60px",
    height: "3px",
    backgroundColor: theme.palette.primary.main,
    borderRadius: "2px 2px 0 0",
  },
});

const headerSettings = [
  { value: "Asset Category", align: "left" },
  { value: "Opening Balance", align: "right" },
  { value: "Additions", align: "right" },
  { value: "Disposal", align: "right" },
  { value: "Closing Balance", align: "right" },
];

const columnSettings = [
  { field: "assetCategory", align: "left", type: "text" },
  { field: "openingBalance", align: "right", type: "numeric" },
  { field: "additions", align: "right", type: "numeric" },
  { field: "disposal", align: "right", type: "numeric" },
  { field: "closingBalance", align: "right", type: "numeric" },
];

const viewOptions = [
  { value: "separate", label: "Separate Mode", icon: ViewModuleIcon },
  { value: "stacked", label: "Stacked Mode", icon: ViewAgendaIcon },
];

const actionButtonSx = {
  p: 0.25,
  mx: 0.25,
  "& svg": { fontSize: 24 },
};

const sumRows = (rows, field) =>
  rows.reduce((acc, row) => acc + Number(row[field] || 0), 0);

const calculateTotalsForRows = (costRows, accDepreciationRows) => {
  const costOpening = sumRows(costRows, "openingBalance");
  const costAdditions = sumRows(costRows, "additions");
  const costDisposal = sumRows(costRows, "disposal");
  const accOpening = sumRows(accDepreciationRows, "openingBalance");
  const accAdditions = sumRows(accDepreciationRows, "additions");
  const accDisposal = sumRows(accDepreciationRows, "disposal");

  return {
    totalOpening: {
      cost: costOpening,
      accDepre: accOpening,
    },
    totalAdditions: {
      cost: costAdditions,
      accDepre: accAdditions,
    },
    totalDisposal: {
      cost: costDisposal,
      accDepre: accDisposal,
    },
    totalClosing: {
      cost: costOpening + costAdditions + costDisposal,
      accDepre: accOpening + accAdditions + accDisposal,
    },
  };
};

const getNumber = (value) => Number(value || 0);
const getCategoryKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const isFaRegisterSourceRow = (row = {}) =>
  row.isImportedFromFARegister || row.faRegisterSource;

const isFaRegisterManualValuesRow = (row = {}) =>
  row.faRegisterManualValues || row.faRegisterValueMode === "manual";

const isFaRegisterLockedRow = (row = {}) =>
  isFaRegisterSourceRow(row) && !isFaRegisterManualValuesRow(row);

const findSummaryRowIndex = (rows, assetId, assetCategory) => {
  if (assetId) {
    const indexById = rows.findIndex((row) => row.assetId === assetId);
    if (indexById !== -1) return indexById;
  }

  const categoryKey = getCategoryKey(assetCategory);
  if (!categoryKey) return -1;

  return rows.findIndex(
    (row) => getCategoryKey(row.assetCategory) === categoryKey
  );
};

const createSummaryRow = (assetId, assetCategory, values, existingRow = {}) => {
  const openingBalance = getNumber(values.openingBalance);
  const additions = getNumber(values.additions);
  const disposal = getNumber(values.disposal);

  return {
    ...existingRow,
    assetId,
    assetCategory,
    openingBalance,
    openingBalanceHistoryAmount: [openingBalance],
    additions,
    additionsHistoryAmount: [additions],
    disposal,
    disposalHistoryAmount: [disposal],
    closingBalance: openingBalance + additions + disposal,
  };
};

const FaSummarySchedule = ({
  company,
  schedule,
  scheduleDetails,
  saveSchedule,
  deleteSchedule,
  canEditSchedule = true,
}) => {
  const theme = useTheme();
  const exportRef = useRef(null);
  const handleExportAsImage = async (e) => {
    e.stopPropagation();
    if (exportRef.current) {
      await exportComponentAsImage(exportRef.current, "FA Summary");
    }
  };

  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [initializing, setInitializing] = useState(true);

  const [excelDialogOpen, setExcelDialogOpen] = useState(false);
  const handleOpenExcelUploadDialog = () => setExcelDialogOpen(true);
  const handleCloseExcelUploadDialog = () => setExcelDialogOpen(false);

  const [confirmRemoveDialog, setConfirmRemoveDialog] = useState(false);
  const [toRemoveAsset, setToRemoveAsset] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [viewMode, setViewMode] = useState("separate");

  const [notes, setNotes] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");

  const [costRows, setCostRows] = useState([]);
  const [accDepreciationRows, setAccDepreciationRows] = useState([]);
  const [totalOpening, setTotalOpening] = useState({
    cost: 0,
    accDepre: 0,
  });
  const [totalAdditions, setTotalAdditions] = useState({
    cost: 0,
    accDepre: 0,
  });
  const [totalDisposal, setTotalDisposal] = useState({
    cost: 0,
    accDepre: 0,
  });
  const [totalClosing, setTotalClosing] = useState({
    cost: 0,
    accDepre: 0,
  });

  const calculateTotal = (rows, type) => {
    // Cost totals
    if (type === COST_TYPE || type === "ALL") {
      const costTotalOpening = rows.cost.reduce(
        (acc, row) => acc + Number(row.openingBalance || 0),
        0
      );
      const costTotalAdditions = rows.cost.reduce(
        (acc, row) => acc + Number(row.additions || 0),
        0
      );
      const costTotalDisposal = rows.cost.reduce(
        (acc, row) => acc + Number(row.disposal || 0),
        0
      );
      const costTotalClosing =
        costTotalOpening + costTotalAdditions + costTotalDisposal;

      setTotalOpening((prev) => ({ ...prev, cost: costTotalOpening }));
      setTotalAdditions((prev) => ({ ...prev, cost: costTotalAdditions }));
      setTotalDisposal((prev) => ({ ...prev, cost: costTotalDisposal }));
      setTotalClosing((prev) => ({ ...prev, cost: costTotalClosing }));
    }

    // Accumulated Depreciation totals
    if (type === ACC_DEPRE_TYPE || type === "ALL") {
      const accDepreTotalOpening = rows.acc.reduce(
        (acc, row) => acc + Number(row.openingBalance || 0),
        0
      );
      const accDepreTotalAdditions = rows.acc.reduce(
        (acc, row) => acc + Number(row.additions || 0),
        0
      );
      const accDepreTotalDisposal = rows.acc.reduce(
        (acc, row) => acc + Number(row.disposal || 0),
        0
      );
      const accDepreTotalClosing =
        accDepreTotalOpening + accDepreTotalAdditions + accDepreTotalDisposal;

      setTotalOpening((prev) => ({ ...prev, accDepre: accDepreTotalOpening }));
      setTotalAdditions((prev) => ({
        ...prev,
        accDepre: accDepreTotalAdditions,
      }));
      setTotalDisposal((prev) => ({
        ...prev,
        accDepre: accDepreTotalDisposal,
      }));
      setTotalClosing((prev) => ({ ...prev, accDepre: accDepreTotalClosing }));
    }
  };

  const buildScheduleDetails = (
    nextCostRows = costRows,
    nextAccDepreciationRows = accDepreciationRows,
    totals = {
      totalOpening,
      totalAdditions,
      totalDisposal,
      totalClosing,
    }
  ) => ({
    costRows: nextCostRows,
    accDepreciationRows: nextAccDepreciationRows,
    totalCostAdditions: totals.totalAdditions["cost"],
    totalCostDisposal: totals.totalDisposal["accDepre"],
    doc: JSON.stringify({
      costRows: nextCostRows,
      accDepreciationRows: nextAccDepreciationRows,
      totalOpening: totals.totalOpening,
      totalAdditions: totals.totalAdditions,
      totalDisposal: totals.totalDisposal,
      totalClosing: totals.totalClosing,
      notes,
      reviewNotes,
    }),
  });

  const saveRows = (nextCostRows, nextAccDepreciationRows, totals) => {
    schedule.notes = notes;
    schedule.reviewNotes = reviewNotes;
    return saveSchedule(
      schedule,
      buildScheduleDetails(nextCostRows, nextAccDepreciationRows, totals)
    );
  };

  const handleSave = () => {
    saveRows(costRows, accDepreciationRows, {
      totalOpening,
      totalAdditions,
      totalDisposal,
      totalClosing,
    });
  };

  const onExcelAddRows = async (uploadedRows) => {
    const nextCostRows = [...costRows];
    const nextAccDepreciationRows = [...accDepreciationRows];

    uploadedRows.forEach((uploadedRow) => {
      const assetCategory = (uploadedRow.assetCategory || "").trim();
      if (!assetCategory) return;

      const costRowIndex = findSummaryRowIndex(
        nextCostRows,
        uploadedRow.assetId,
        assetCategory
      );
      const existingCostRow =
        costRowIndex !== -1 ? nextCostRows[costRowIndex] : {};
      const accDepreciationRowIndex = findSummaryRowIndex(
        nextAccDepreciationRows,
        uploadedRow.assetId || existingCostRow.assetId,
        assetCategory
      );
      const existingAccDepreciationRow =
        accDepreciationRowIndex !== -1
          ? nextAccDepreciationRows[accDepreciationRowIndex]
          : {};
      const assetId =
        uploadedRow.assetId ||
        existingCostRow.assetId ||
        existingAccDepreciationRow.assetId ||
        uuidv4();
      const costRow = createSummaryRow(
        assetId,
        assetCategory,
        uploadedRow.cost,
        existingCostRow
      );

      if (costRowIndex !== -1) {
        nextCostRows[costRowIndex] = costRow;
      } else {
        nextCostRows.push(costRow);
      }

      const accDepreciationRow = createSummaryRow(
        assetId,
        assetCategory,
        uploadedRow.accumulatedDepreciation,
        existingAccDepreciationRow
      );

      if (accDepreciationRowIndex !== -1) {
        nextAccDepreciationRows[accDepreciationRowIndex] = accDepreciationRow;
      } else {
        nextAccDepreciationRows.push(accDepreciationRow);
      }
    });

    const totals = calculateTotalsForRows(
      nextCostRows,
      nextAccDepreciationRows
    );

    setCostRows(nextCostRows);
    setAccDepreciationRows(nextAccDepreciationRows);
    setTotalOpening(totals.totalOpening);
    setTotalAdditions(totals.totalAdditions);
    setTotalDisposal(totals.totalDisposal);
    setTotalClosing(totals.totalClosing);
    const saved = await saveRows(nextCostRows, nextAccDepreciationRows, totals);
    if (saved === false) return false;
    handleCloseExcelUploadDialog();
    return true;
  };

  const handleInputChange = (
    value,
    { assetId, field, field_type, type, setRows }
  ) => {
    let toUpdateRows = type === COST_TYPE ? costRows : accDepreciationRows;

    if (toUpdateRows.length > 0) {
      const updatedRows = [...toUpdateRows];

      // Find row index by assetId
      const rowIndex = updatedRows.findIndex((row) => row.assetId === assetId);
      if (rowIndex === -1) return; // asset not found, skip
      if (field_type === "numeric") {
        // CalculatorTextField passes an array for historyAmount
        const historyAmountField = `${field}HistoryAmount`;
        const calculatedAmount = Array.isArray(value)
          ? value.reduce((sum, num) => sum + num, 0)
          : Number(getCommaOut(value));

        // Update historyAmount and amount
        updatedRows[rowIndex][historyAmountField] = Array.isArray(value)
          ? value
          : [calculatedAmount];
        updatedRows[rowIndex][field] = calculatedAmount;

        let { openingBalance, additions, disposal } =
          updatedRows[rowIndex] || {};

        // Recalculate closing balance
        updatedRows[rowIndex].closingBalance =
          (openingBalance || 0) + (additions || 0) + (disposal || 0);
      } else {
        // Update the specific field for non-numeric fields
        updatedRows[rowIndex] = { ...updatedRows[rowIndex], [field]: value };
      }

      setRows(updatedRows);

      // Recalculate totals when numbers change
      if (field_type === "numeric") {
        const costs = type === COST_TYPE ? updatedRows : costRows;
        const accDepres =
          type === ACC_DEPRE_TYPE ? updatedRows : accDepreciationRows;
        calculateTotal({ cost: costs, acc: accDepres }, type);
      }

      // Keep assetCategory synced between cost & accDepre rows
      if (field === "assetCategory" && type === COST_TYPE) {
        const accDepreUpdatedRows = [...accDepreciationRows];

        const accRowIndex = accDepreUpdatedRows.findIndex(
          (row) => row.assetId === assetId
        );

        if (accRowIndex !== -1) {
          accDepreUpdatedRows[accRowIndex] = {
            ...accDepreUpdatedRows[accRowIndex],
            [field]: value,
          };
        }

        setAccDepreciationRows(accDepreUpdatedRows);
      }
    }
  };

  const handleMakeFaRegisterRowEditable = (assetId) => {
    const markManualValues = (row) =>
      row.assetId === assetId
        ? {
            ...row,
            faRegisterManualValues: true,
            faRegisterValueMode: "manual",
          }
        : row;

    setCostRows((prevRows) => prevRows.map(markManualValues));
    setAccDepreciationRows((prevRows) => prevRows.map(markManualValues));
  };

  const getFaRegisterSourceAmount = (row, field) =>
    Number(row?.faRegisterSourceSnapshot?.[field] ?? 0);

  const hasFaRegisterSourceAmount = (row, field) =>
    row?.faRegisterSourceSnapshot &&
    row.faRegisterSourceSnapshot[field] !== undefined &&
    row.faRegisterSourceSnapshot[field] !== null;

  const refreshFaRegisterSourceAmount = (row, fieldIndex) => {
    const field = columnSettings[fieldIndex].field;
    if (!hasFaRegisterSourceAmount(row, field)) return;

    handleInputChange([getFaRegisterSourceAmount(row, field)], {
      assetId: row.assetId,
      field,
      field_type: columnSettings[fieldIndex].type,
      type: COST_TYPE,
      setRows: setCostRows,
    });
  };

  const handleUseFaRegisterSourceValues = (assetId) => {
    const nextCostRows = costRows.map((row) => {
      if (row.assetId !== assetId) return row;

      const nextRow = {
        ...row,
        faRegisterManualValues: false,
        faRegisterValueMode: "source",
      };

      ["openingBalance", "additions", "disposal"].forEach((field) => {
        if (!hasFaRegisterSourceAmount(row, field)) return;
        const sourceAmount = getFaRegisterSourceAmount(row, field);
        nextRow[field] = sourceAmount;
        nextRow[`${field}HistoryAmount`] = [sourceAmount];
      });

      nextRow.closingBalance =
        (nextRow.openingBalance || 0) +
        (nextRow.additions || 0) +
        (nextRow.disposal || 0);

      return nextRow;
    });

    const nextAccDepreciationRows = accDepreciationRows.map((row) =>
      row.assetId === assetId
        ? {
            ...row,
            faRegisterManualValues: false,
            faRegisterValueMode: "source",
          }
        : row
    );

    setCostRows(nextCostRows);
    setAccDepreciationRows(nextAccDepreciationRows);
    calculateTotal({ cost: nextCostRows, acc: nextAccDepreciationRows }, "ALL");
  };

  const renderCalculatorAmountCell = ({
    row,
    fieldIndex,
    type,
    setRows,
    canEdit = canEditSchedule,
    showSourceRefresh = false,
    refreshTooltip,
  }) => {
    const field = columnSettings[fieldIndex].field;
    const historyAmountField = `${field}HistoryAmount`;
    const historyTitle =
      row?.[historyAmountField] &&
      row[historyAmountField].length > 1 &&
      formatHistoryAmount(row[historyAmountField]);
    const input = (
      <Tooltip title={historyTitle} placement='top'>
        <Box display='inline-block' sx={{ width: "100%" }}>
          <CalculatorTextField
            value={row?.[historyAmountField] || [0]}
            onChange={handleInputChange}
            reference={{
              assetId: row.assetId,
              field,
              field_type: columnSettings[fieldIndex].type,
              type,
              setRows,
            }}
            canEdit={canEdit}
          />
        </Box>
      </Tooltip>
    );

    if (
      !showSourceRefresh ||
      !canEditSchedule ||
      !isFaRegisterManualValuesRow(row)
    ) {
      return input;
    }

    return (
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='flex-end'
        spacing={0.75}
        sx={{ width: "100%" }}
      >
        <StyledIconButton
          icon={RefreshIcon}
          onClick={() => refreshFaRegisterSourceAmount(row, fieldIndex)}
          tooltip={refreshTooltip || "Refresh from FA Register"}
          iconColor='primary.main'
          disabled={!hasFaRegisterSourceAmount(row, field)}
        />
        <Box sx={{ minWidth: 0 }}>{input}</Box>
      </Stack>
    );
  };

  const renderImportedInfo = (row) =>
    isFaRegisterSourceRow(row) ? (
      <>
        <JustInfo
          title={
            isFaRegisterManualValuesRow(row)
              ? "Imported from FA Register. Source is retained, but FA Summary values are maintained manually and will not refresh from FA Register."
              : "Imported from FA Register. Cost values are maintained in FA Register. Click the edit icon to maintain FA Summary values manually."
          }
          ml={0.75}
          mr={0}
        />
      </>
    ) : null;

  const renderActionCellContent = (row) => {
    if (isFaRegisterLockedRow(row)) {
      return (
        <IconButtonWithTooltip
          icon={EditOutlinedIcon}
          tooltipTitle='Maintain FA Summary values manually'
          onClick={() => handleMakeFaRegisterRowEditable(row.assetId)}
          color='primary'
          size='small'
          sx={actionButtonSx}
        />
      );
    }

    if (isFaRegisterSourceRow(row) && isFaRegisterManualValuesRow(row)) {
      return (
        <IconButtonWithTooltip
          icon={LinkIcon}
          tooltipTitle='Use FA Register values again'
          onClick={() => handleUseFaRegisterSourceValues(row.assetId)}
          color='info'
          size='small'
          sx={actionButtonSx}
        />
      );
    }

    return (
      <IconButtonWithTooltip
        icon={DeleteOutlineIcon}
        tooltipTitle='Delete Row'
        onClick={() => handleRemoveRow(row.assetId)}
        color='error'
        size='small'
        sx={actionButtonSx}
      />
    );
  };

  const renderActionCell = (row, tableCellProps = {}) =>
    canEditSchedule ? (
      <TableCell align='center' {...tableCellProps}>
        {renderActionCellContent(row)}
      </TableCell>
    ) : null;

  const renderAssetCategoryCell = (row, canEditName = false) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {canEditName && !isFaRegisterSourceRow(row) ? (
          <TextFieldForName
            value={row.assetCategory}
            onChange={handleInputChange}
            reference={{
              assetId: row.assetId,
              field: columnSettings[0].field,
              field_type: columnSettings[0].type,
              type: COST_TYPE,
              setRows: setCostRows,
            }}
            canEdit={canEditSchedule}
          />
        ) : (
          <TextFieldForViewOnly value={row.assetCategory} />
        )}
      </Box>
      {renderImportedInfo(row)}
    </Box>
  );

  const renderAdditionsCell = (row, type, setRows, canEdit = canEditSchedule) =>
    renderCalculatorAmountCell({
      row,
      fieldIndex: 2,
      type,
      setRows,
      canEdit,
      showSourceRefresh: type === COST_TYPE,
      refreshTooltip: "Refresh additions from FA Register",
    });

  useEffect(() => {
    setInitializing(true);

    //init
    if (schedule) {
      setNotes(schedule.notes);
      setReviewNotes(schedule.reviewNotes);
    }

    if (scheduleDetails) {
      // Handle data from dataRows structure (transformed by backend)
      if (scheduleDetails.dataRows && scheduleDetails.dataRows.length > 0) {
        const costRows = scheduleDetails.dataRows.map((row) => {
          const costData = row.cost || {};

          return {
            assetId: row.assetId,
            assetCategory: row.assetCategory,
            isImportedFromFARegister: row.isImportedFromFARegister || false,
            faRegisterSource: row.faRegisterSource || false,
            faRegisterManualValues:
              row.faRegisterManualValues ||
              row.faRegisterValueMode === "manual" ||
              false,
            faRegisterValueMode: row.faRegisterValueMode,
            faRegisterSourceSnapshot: row.faRegisterSourceSnapshot || null,
            openingBalance: costData.openingBalance,
            openingBalanceHistoryAmount: costData.openingBalanceHistoryAmount,
            additions: costData.additions,
            additionsHistoryAmount: costData.additionsHistoryAmount,
            disposal: costData.disposal,
            disposalHistoryAmount: costData.disposalHistoryAmount,
            closingBalance:
              costData.openingBalance + costData.additions + costData.disposal,
          };
        });

        const accDepreciationRows = scheduleDetails.dataRows.map((row) => {
          const accData = row.accumulatedDepreciation || {};

          return {
            assetId: row.assetId,
            assetCategory: row.assetCategory,
            isImportedFromFARegister: row.isImportedFromFARegister || false,
            faRegisterSource: row.faRegisterSource || false,
            faRegisterManualValues:
              row.faRegisterManualValues ||
              row.faRegisterValueMode === "manual" ||
              false,
            faRegisterValueMode: row.faRegisterValueMode,
            faRegisterSourceSnapshot: row.faRegisterSourceSnapshot || null,
            openingBalance: accData.openingBalance,
            openingBalanceHistoryAmount: accData.openingBalanceHistoryAmount,
            additions: accData.additions,
            additionsHistoryAmount: accData.additionsHistoryAmount,
            disposal: accData.disposal,
            disposalHistoryAmount: accData.disposalHistoryAmount,
            closingBalance:
              accData.openingBalance + accData.additions + accData.disposal,
          };
        });

        setCostRows(costRows);
        setAccDepreciationRows(accDepreciationRows);

        // Calculate totals
        calculateTotal({ cost: costRows, acc: accDepreciationRows }, "ALL");
      }
    }

    setInitializing(false);
  }, []);

  const addNewRow = () => {
    const newAssetId = uuidv4();

    setCostRows((prevRows) => [
      ...prevRows,
      {
        assetId: newAssetId,
        assetCategory: "",
        openingBalance: 0,
        openingBalanceHistoryAmount: [0],
        additions: 0,
        additionsHistoryAmount: [0],
        disposal: 0,
        disposalHistoryAmount: [0],
        closingBalance: 0,
      },
    ]);

    setAccDepreciationRows((prevRows) => [
      ...prevRows,
      {
        assetId: newAssetId,
        assetCategory: "",
        openingBalance: 0,
        openingBalanceHistoryAmount: [0],
        additions: 0,
        additionsHistoryAmount: [0],
        disposal: 0,
        disposalHistoryAmount: [0],
        closingBalance: 0,
      },
    ]);
  };

  const handleRemoveRow = (assetId) => {
    setConfirmRemoveDialog(true);
    setToRemoveAsset(assetId);
  };

  const handleCancelRemove = () => {
    setConfirmRemoveDialog(false);
    setToRemoveAsset(null);
    setRemoving(false);
  };

  const handleConfirmRemove = () => {
    const assetId = toRemoveAsset;
    setRemoving(true);
    // Remove row from costRows based on assetId
    let updatedCostRows = costRows.filter((row) => row.assetId !== assetId);
    setCostRows(updatedCostRows);

    // Remove the corresponding row from accDepreciationRows based on assetId
    let updatedAccDepRows = accDepreciationRows.filter(
      (row) => row.assetId !== assetId
    );
    setAccDepreciationRows(updatedAccDepRows);

    // Recalculate totals
    calculateTotal({ cost: updatedCostRows, acc: updatedAccDepRows }, "ALL");

    handleCancelRemove();
  };

  const accDepMap = useMemo(() => {
    return accDepreciationRows.reduce((map, row) => {
      if (row.assetId) {
        map[row.assetId] = row;
      }
      return map;
    }, {});
  }, [accDepreciationRows]);

  const renderMainContent = () => (
    <div style={{ width: "100%" }}>
      <Toolbar
        title={`Schedule ${schedule.scheduleNo}`}
        titleInfo='The FA Summary is the master list of asset categories, showing opening balance, additions, disposals, and closing balance for cost and accumulated depreciation.'
        subtitle={schedule.type}
        onToggleFullscreen={() => setFullscreenOpen(!fullscreenOpen)}
        fullscreenOpen={fullscreenOpen}
        viewMode={viewMode}
        viewOptions={viewOptions}
        onToggleView={setViewMode}
        onAdd={addNewRow}
        size='medium'
        showActionBar
        allowSticky
        handleExportAsImage={handleExportAsImage}
        exportRef={exportRef}
        canEdit={canEditSchedule}
      />
      <Paper
        ref={exportRef}
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          bgcolor: (theme) => alpha(theme.palette.background.paper, 0.98),
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          minHeight: fullscreenOpen ? "88vh" : "45vh",
        }}
      >
        {initializing ? (
          <ComponentLoading />
        ) : (
          <>
            {costRows.length > 0 ? (
              <TableContainer
                component={Paper}
                sx={{
                  border: "none",
                  boxShadow: "none",
                }}
              >
                <Table stickyHeader>
                  <TableHead sx={getHeaderStyles(theme, false)}>
                    <TableRow>
                      {headerSettings.map((header, index) => (
                        <TableCell
                          align={header.align}
                          style={index === 0 ? { width: 250 } : {}}
                        >
                          {header.value}
                          {index === 0 && viewMode === "stacked" && (
                            <JustInfo
                              title={
                                <>
                                  <div>Top row: Cost values</div>
                                  <div>
                                    Second row: <br />
                                    Accumulated Depreciation values
                                  </div>
                                </>
                              }
                            />
                          )}
                        </TableCell>
                      ))}
                      {canEditSchedule && (
                        <TableCell align='center'>Actions</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  {viewMode === "separate" ? (
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={6} style={sectionStyle(theme)}>
                          Cost
                        </TableCell>
                      </TableRow>
                      {costRows.map((row, i) => (
                        <TableRow key={row.assetId}>
                          <TableCell align={columnSettings[0].align}>
                            {renderAssetCategoryCell(row, true)}
                          </TableCell>
                          <TableCell align={columnSettings[1].align}>
                            {renderCalculatorAmountCell({
                              row,
                              fieldIndex: 1,
                              type: COST_TYPE,
                              setRows: setCostRows,
                              canEdit:
                                canEditSchedule && !isFaRegisterLockedRow(row),
                              showSourceRefresh: true,
                              refreshTooltip:
                                "Refresh opening balance from FA Register",
                            })}
                          </TableCell>
                          <TableCell align={columnSettings[2].align}>
                            {renderAdditionsCell(
                              row,
                              COST_TYPE,
                              setCostRows,
                              canEditSchedule && !isFaRegisterLockedRow(row)
                            )}
                          </TableCell>
                          <TableCell align={columnSettings[3].align}>
                            {renderCalculatorAmountCell({
                              row,
                              fieldIndex: 3,
                              type: COST_TYPE,
                              setRows: setCostRows,
                              canEdit:
                                canEditSchedule && !isFaRegisterLockedRow(row),
                              showSourceRefresh: true,
                              refreshTooltip:
                                "Refresh disposal from FA Register",
                            })}
                          </TableCell>
                          <TableCell align={columnSettings[4].align}>
                            <TextFieldForViewOnly
                              value={formatAmount(row.closingBalance)}
                            />
                          </TableCell>
                          {renderActionCell(row)}
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            color: "text.primary",
                          }}
                        >
                          Total Cost
                        </TableCell>
                        <TableCell align='right'>
                          <TextFieldForViewOnly
                            value={formatAmount(totalOpening.cost)}
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <TextFieldForViewOnly
                            value={formatAmount(totalAdditions.cost)}
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <TextFieldForViewOnly
                            value={formatAmount(totalDisposal.cost)}
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <TextFieldForViewOnly
                            value={formatAmount(totalClosing.cost)}
                          />
                        </TableCell>
                        {canEditSchedule && <TableCell></TableCell>}
                      </TableRow>

                      <TableRow>
                        <TableCell colSpan={6} style={sectionStyle(theme)}>
                          Accumulated Depreciation
                        </TableCell>
                      </TableRow>
                      {accDepreciationRows.map((row, i) => (
                        <TableRow key={row.assetId}>
                          <TableCell align={columnSettings[0].align}>
                            {renderAssetCategoryCell(row)}
                          </TableCell>
                          <TableCell align={columnSettings[1].align}>
                            <Tooltip
                              title={
                                row?.openingBalanceHistoryAmount &&
                                row.openingBalanceHistoryAmount.length > 1 &&
                                formatHistoryAmount(
                                  row.openingBalanceHistoryAmount
                                )
                              }
                              placement='top'
                            >
                              <Box
                                display='inline-block'
                                sx={{ width: "100%" }}
                              >
                                <CalculatorTextField
                                  value={
                                    row?.openingBalanceHistoryAmount || [0]
                                  }
                                  onChange={handleInputChange}
                                  reference={{
                                    assetId: row.assetId,
                                    field: columnSettings[1].field,
                                    field_type: columnSettings[1].type,
                                    type: ACC_DEPRE_TYPE,
                                    setRows: setAccDepreciationRows,
                                  }}
                                  canEdit={canEditSchedule}
                                />
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell align={columnSettings[2].align}>
                            {renderAdditionsCell(
                              row,
                              ACC_DEPRE_TYPE,
                              setAccDepreciationRows
                            )}
                          </TableCell>
                          <TableCell align={columnSettings[3].align}>
                            <Tooltip
                              title={
                                row?.disposalHistoryAmount &&
                                row.disposalHistoryAmount.length > 1 &&
                                formatHistoryAmount(row.disposalHistoryAmount)
                              }
                              placement='top'
                            >
                              <Box
                                display='inline-block'
                                sx={{ width: "100%" }}
                              >
                                <CalculatorTextField
                                  value={row?.disposalHistoryAmount || [0]}
                                  onChange={handleInputChange}
                                  reference={{
                                    assetId: row.assetId,
                                    field: columnSettings[3].field,
                                    field_type: columnSettings[3].type,
                                    type: ACC_DEPRE_TYPE,
                                    setRows: setAccDepreciationRows,
                                  }}
                                  canEdit={canEditSchedule}
                                />
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell align={columnSettings[4].align}>
                            <TextFieldForViewOnly
                              value={formatAmount(row.closingBalance)}
                            />
                          </TableCell>
                          {canEditSchedule && (
                            <TableCell align='center'></TableCell>
                          )}
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            color: "text.primary",
                          }}
                        >
                          Total Accumulated Depreciation
                        </TableCell>
                        <TableCell align='right'>
                          <TextFieldForViewOnly
                            value={formatAmount(totalOpening.accDepre)}
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <TextFieldForViewOnly
                            value={formatAmount(totalAdditions.accDepre)}
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <TextFieldForViewOnly
                            value={formatAmount(totalDisposal.accDepre)}
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <TextFieldForViewOnly
                            value={formatAmount(totalClosing.accDepre)}
                          />
                        </TableCell>
                        {canEditSchedule && <TableCell></TableCell>}
                      </TableRow>
                    </TableBody>
                  ) : (
                    <TableBody>
                      {costRows.map((row, i) => (
                        <React.Fragment key={row.assetId}>
                          {/* Cost row */}
                          <TableRow>
                            {/* Asset Category (only shown in cost row) */}
                            <TableCell
                              align={columnSettings[0].align}
                              rowSpan={2}
                            >
                              {renderAssetCategoryCell(row, true)}
                            </TableCell>

                            {/* Cost: Opening, Additions, Disposal, Closing */}
                            <TableCell align={columnSettings[1].align}>
                              {renderCalculatorAmountCell({
                                row,
                                fieldIndex: 1,
                                type: COST_TYPE,
                                setRows: setCostRows,
                                canEdit:
                                  canEditSchedule &&
                                  !isFaRegisterLockedRow(row),
                                showSourceRefresh: true,
                                refreshTooltip:
                                  "Refresh opening balance from FA Register",
                              })}
                            </TableCell>
                            <TableCell align={columnSettings[2].align}>
                              {renderAdditionsCell(
                                row,
                                COST_TYPE,
                                setCostRows,
                                canEditSchedule && !isFaRegisterLockedRow(row)
                              )}
                            </TableCell>
                            <TableCell align={columnSettings[3].align}>
                              {renderCalculatorAmountCell({
                                row,
                                fieldIndex: 3,
                                type: COST_TYPE,
                                setRows: setCostRows,
                                canEdit:
                                  canEditSchedule &&
                                  !isFaRegisterLockedRow(row),
                                showSourceRefresh: true,
                                refreshTooltip:
                                  "Refresh disposal from FA Register",
                              })}
                            </TableCell>
                            <TableCell align={columnSettings[4].align}>
                              <TextFieldForViewOnly
                                value={formatAmount(row.closingBalance)}
                              />
                            </TableCell>

                            {renderActionCell(row, { rowSpan: 2 })}
                          </TableRow>

                          {/* Accumulated Depreciation row */}
                          <TableRow>
                            <TableCell align={columnSettings[1].align}>
                              <Tooltip
                                title={
                                  accDepMap[row.assetId]
                                    ?.openingBalanceHistoryAmount &&
                                  accDepMap[row.assetId]
                                    .openingBalanceHistoryAmount.length > 1 &&
                                  formatHistoryAmount(
                                    accDepMap[row.assetId]
                                      .openingBalanceHistoryAmount
                                  )
                                }
                                placement='top'
                              >
                                <Box
                                  display='inline-block'
                                  sx={{ width: "100%" }}
                                >
                                  <CalculatorTextField
                                    value={
                                      accDepMap[row.assetId]
                                        ?.openingBalanceHistoryAmount || [0]
                                    }
                                    onChange={handleInputChange}
                                    reference={{
                                      assetId: row.assetId,
                                      field: columnSettings[1].field,
                                      field_type: columnSettings[1].type,
                                      type: ACC_DEPRE_TYPE,
                                      setRows: setAccDepreciationRows,
                                    }}
                                    canEdit={canEditSchedule}
                                  />
                                </Box>
                              </Tooltip>
                            </TableCell>
                            <TableCell align={columnSettings[2].align}>
                              {renderAdditionsCell(
                                accDepMap[row.assetId] || row,
                                ACC_DEPRE_TYPE,
                                setAccDepreciationRows
                              )}
                            </TableCell>
                            <TableCell align={columnSettings[3].align}>
                              <Tooltip
                                title={
                                  accDepMap[row.assetId]
                                    ?.disposalHistoryAmount &&
                                  accDepMap[row.assetId].disposalHistoryAmount
                                    .length > 1 &&
                                  formatHistoryAmount(
                                    accDepMap[row.assetId].disposalHistoryAmount
                                  )
                                }
                                placement='top'
                              >
                                <Box
                                  display='inline-block'
                                  sx={{ width: "100%" }}
                                >
                                  <CalculatorTextField
                                    value={
                                      accDepMap[row.assetId]
                                        ?.disposalHistoryAmount || [0]
                                    }
                                    onChange={handleInputChange}
                                    reference={{
                                      assetId: row.assetId,
                                      field: columnSettings[3].field,
                                      field_type: columnSettings[3].type,
                                      type: ACC_DEPRE_TYPE,
                                      setRows: setAccDepreciationRows,
                                    }}
                                    canEdit={canEditSchedule}
                                  />
                                </Box>
                              </Tooltip>
                            </TableCell>
                            <TableCell align={columnSettings[4].align}>
                              <TextFieldForViewOnly
                                value={formatAmount(
                                  accDepMap[row.assetId]?.closingBalance || 0
                                )}
                              />
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))}

                      {/* Totals Row (stacked like asset rows) */}
                      <TableRow>
                        {/* First cell spans 2 rows */}
                        <TableCell
                          sx={{ fontWeight: 700, color: "text.primary" }}
                        >
                          Total Cost
                        </TableCell>

                        {/* Cost Totals */}
                        <TableCell align='right'>
                          <TextFieldForViewOnly
                            value={formatAmount(totalOpening.cost)}
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <TextFieldForViewOnly
                            value={formatAmount(totalAdditions.cost)}
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <TextFieldForViewOnly
                            value={formatAmount(totalDisposal.cost)}
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <TextFieldForViewOnly
                            value={formatAmount(totalClosing.cost)}
                          />
                        </TableCell>

                        {canEditSchedule && (
                          <TableCell align='center'></TableCell>
                        )}
                      </TableRow>

                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 700, color: "text.primary" }}
                        >
                          Total Accumulated Depreciation
                        </TableCell>
                        {/* Accumulated Depreciation Totals */}
                        <TableCell align='right'>
                          <TextFieldForViewOnly
                            value={formatAmount(totalOpening.accDepre)}
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <TextFieldForViewOnly
                            value={formatAmount(totalAdditions.accDepre)}
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <TextFieldForViewOnly
                            value={formatAmount(totalDisposal.accDepre)}
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <TextFieldForViewOnly
                            value={formatAmount(totalClosing.accDepre)}
                          />
                        </TableCell>
                        {canEditSchedule && (
                          <TableCell align='center'></TableCell>
                        )}
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            ) : (
              <NoDataAvailableUI
                title='No Categories Defined'
                content="You haven't created any categories yet."
                subContent='Please define at least one category to get started.'
                onAddNew={addNewRow}
              />
            )}
          </>
        )}
      </Paper>
    </div>
  );

  return (
    <Box sx={{ padding: 4 }}>
      <ScheduleHeader
        {...{
          title: schedule.type,
          companyName: company.name,
          uen: company.uen,
          yearOfAssessment: company.yearOfAssessment,
          financialYearStart: company.financialYearStart,
          financialYearEnd: company.financialYearEnd,
          handleSave,
          handleExcel: handleOpenExcelUploadDialog,
          deleteSchedule,
          canEditSchedule,
        }}
      >
        <PdfGenerator
          schedule={schedule}
          company={company}
          costRows={costRows}
          accDepreciationRows={accDepreciationRows}
          totalOpening={totalOpening}
          totalAdditions={totalAdditions}
          totalDisposal={totalDisposal}
          totalClosing={totalClosing}
          notes={notes}
          reviewNotes={reviewNotes}
        />
      </ScheduleHeader>
      <br />

      {!fullscreenOpen && renderMainContent()}

      <Divider sx={{ marginY: 4 }} />
      <ScheduleNotes
        notes={notes}
        setNotes={setNotes}
        reviewNotes={reviewNotes}
        setReviewNotes={setReviewNotes}
        enableEdit={canEditSchedule}
      />

      <FaSummaryExcelUpload
        existingCategories={costRows}
        onExcelAddRows={onExcelAddRows}
        open={excelDialogOpen}
        handleCloseDialog={handleCloseExcelUploadDialog}
      />

      <CustomDialog
        open={confirmRemoveDialog}
        onClose={handleCancelRemove}
        onConfirm={handleConfirmRemove}
        title='Remove Category'
        description={
          <>
            Removing this asset category will permanently delete all assets
            under it. <strong>This action cannot be undone.</strong>
            <br />
            Are you sure you want to continue?
          </>
        }
        type='error'
        loading={removing}
      />

      <FullscreenDialog
        open={fullscreenOpen}
        onClose={() => setFullscreenOpen(false)}
        title={schedule.type}
        subtitle={`${company.name} (${company.uen}) - YA ${company.yearOfAssessment}`}
      >
        {renderMainContent()}
      </FullscreenDialog>
    </Box>
  );
};

export default FaSummarySchedule;
